import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { twsAPI } from '../services/api';
import { queryKeys } from '../config/queryClient';

/**
 * Hook pour récupérer le statut de connexion IBKR/TWS
 * Polling automatique toutes les 30 secondes pour un monitoring plus réactif
 * Le keep-alive backend envoie des pings toutes les 60s, donc on vérifie toutes les 30s
 */
export const useIBKRStatus = (options = {}) => {
  return useQuery({
    queryKey: queryKeys.ibkrStatus(),
    queryFn: async () => {
      const response = await twsAPI.getStatus();
      return response.data;
    },
    staleTime: 90 * 1000, // 90 secondes
    refetchInterval: 2 * 60 * 1000, // 2 minutes (réduit la charge)
    refetchOnWindowFocus: false,
    retry: (failureCount, error) => {
      // Ne pas retry sur erreur 503 (TWS non connecté)
      if (error?.response?.status === 503) {
        return false;
      }
      return failureCount < 2;
    },
    ...options,
  });
};

/**
 * Hook pour récupérer les positions IBKR
 * Polling automatique toutes les 30 secondes
 * Ne s'exécute que si TWS est connecté
 * @param {Object} options - Options de la query React Query
 * @param {boolean} options.isConnected - Statut de connexion TWS (optionnel, vérifié automatiquement si non fourni)
 */
export const usePositions = (options = {}) => {
  // Toujours appeler useIBKRStatus (règle des hooks React)
  const { data: statusData } = useIBKRStatus();
  
  // Utiliser isConnected des options s'il est fourni, sinon utiliser le statut de la query
  const isConnected = options.isConnected !== undefined 
    ? options.isConnected 
    : (statusData?.is_connected ?? false);
  
  // Extraire enabled des options pour permettre de le surcharger
  const { enabled: optionsEnabled, isConnected: _, ...restOptions } = options;
  
  return useQuery({
    queryKey: queryKeys.positions(),
    queryFn: async () => {
      const response = await twsAPI.getPositions();
      return response.data || [];
    },
    enabled: isConnected && (optionsEnabled !== false), // Ne s'exécute que si connecté
    staleTime: 90 * 1000, // 90 secondes
    refetchInterval: isConnected ? 2 * 60 * 1000 : false, // 2 min si connecté
    refetchOnWindowFocus: false,
    placeholderData: (previousData) => previousData,
    retry: (failureCount, error) => {
      if (error?.response?.status === 503) return false;
      return failureCount < 2;
    },
    ...restOptions,
  });
};

/**
 * Hook pour récupérer les données du compte IBKR
 * Polling automatique toutes les 30 secondes
 * Ne s'exécute que si TWS est connecté
 * @param {Object} options - Options de la query React Query
 * @param {boolean} options.isConnected - Statut de connexion TWS (optionnel, vérifié automatiquement si non fourni)
 */
export const useAccount = (options = {}) => {
  // Toujours appeler useIBKRStatus (règle des hooks React)
  const { data: statusData } = useIBKRStatus();
  
  // Utiliser isConnected des options s'il est fourni, sinon utiliser le statut de la query
  const isConnected = options.isConnected !== undefined 
    ? options.isConnected 
    : (statusData?.is_connected ?? false);
  
  // Extraire enabled des options pour permettre de le surcharger
  const { enabled: optionsEnabled, isConnected: _, ...restOptions } = options;
  
  return useQuery({
    queryKey: queryKeys.account(),
    queryFn: async () => {
      const response = await twsAPI.getAccount();
      return response.data;
    },
    enabled: isConnected && (optionsEnabled !== false), // Ne s'exécute que si connecté
    staleTime: 90 * 1000, // 90 secondes
    refetchInterval: isConnected ? 2 * 60 * 1000 : false, // 2 min si connecté
    refetchOnWindowFocus: false,
    placeholderData: (previousData) => previousData,
    retry: (failureCount, error) => {
      if (error?.response?.status === 503) return false;
      return failureCount < 2;
    },
    ...restOptions,
  });
};

/**
 * Hook pour récupérer le résumé du compte IBKR
 */
export const useAccountSummary = (options = {}) => {
  return useQuery({
    queryKey: queryKeys.accountSummary(),
    queryFn: async () => {
      const response = await twsAPI.getAccountSummary();
      return response.data;
    },
    staleTime: 90 * 1000, // 90 secondes
    retry: (failureCount, error) => {
      if (error?.response?.status === 503) {
        return false;
      }
      return failureCount < 2;
    },
    ...options,
  });
};

/**
 * Hook pour récupérer les ordres IBKR
 */
export const useOrders = (options = {}) => {
  return useQuery({
    queryKey: queryKeys.orders(),
    queryFn: async () => {
      const response = await twsAPI.getOrders();
      return response.data || [];
    },
    staleTime: 90 * 1000, // 90 secondes
    retry: (failureCount, error) => {
      if (error?.response?.status === 503) {
        return false;
      }
      return failureCount < 2;
    },
    ...options,
  });
};

/**
 * Hook pour connecter à IBKR/TWS
 */
export const useConnectIBKR = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async () => {
      const response = await twsAPI.connect();
      return response.data;
    },
    onSuccess: () => {
      // Invalider les queries pour forcer le rechargement
      queryClient.invalidateQueries({ queryKey: queryKeys.ibkrStatus() });
      queryClient.invalidateQueries({ queryKey: queryKeys.positions() });
      queryClient.invalidateQueries({ queryKey: queryKeys.account() });
    },
  });
};

/**
 * Hook pour déconnecter d'IBKR/TWS
 */
export const useDisconnectIBKR = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async () => {
      const response = await twsAPI.disconnect();
      return response.data;
    },
    onSuccess: () => {
      // Invalider et vider les queries
      queryClient.invalidateQueries({ queryKey: queryKeys.ibkrStatus() });
      queryClient.setQueryData(queryKeys.positions(), []);
      queryClient.setQueryData(queryKeys.account(), null);
    },
  });
};

